import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { sanitizeHtml } from '@/lib/html-utils';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteRoles } from '@/hooks/useSiteRoles';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';

import { ImageCarousel } from '@/components/ImageCarousel';
import { ImageItem, jsonToImageArray } from '@/lib/image-utils';
import { WorkflowProgressBar } from '@/components/WorkflowProgressBar';
import { SubstepsSidebar } from '@/components/SubstepsSidebar';
import { WorkflowLayout } from '@/components/layout/WorkflowLayout';

interface WorkInstruction {
  id: string;
  title: string;
  description: string;
  revision: number;
  status: string;
}

interface InstructionStep {
  id: string;
  step_number: number;
  title: string;
  description: string;
  image_url: ImageItem[];
  is_quality_check: boolean;
  evidence_required: boolean;
  parent_step_id?: string | null;
  part_number?: string | null;
  tools_equipment?: string | null;
  ppe?: string | null;
  tools_equipment_image_url?: ImageItem[];
}

const InstructionPreview = () => {
  const { instructionId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentTenantId, currentLineId } = useAuth();
  const { hasPermission, isReady: rolesReady } = useSiteRoles(currentLineId || undefined);
  
  const [instruction, setInstruction] = useState<WorkInstruction | null>(null);
  const [steps, setSteps] = useState<InstructionStep[]>([]);
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  usePermissionGuard({
    enabled: rolesReady && !!currentLineId,
    allowed: hasPermission('core.instructions.view'),
    message: t('dashboard.permissionAccessDenied'),
  });

  // Organize steps into main steps and their substeps
  const stepsStructure = useMemo(() => {
    const mainSteps = steps.filter(s => !s.parent_step_id);
    return mainSteps.map(mainStep => ({
      mainStep,
      substeps: steps.filter(s => s.parent_step_id === mainStep.id),
    }));
  }, [steps]);

  // Find current main step and substep
  const currentStepInfo = useMemo(() => {
    if (!currentStepId) return null;

    const currentStep = steps.find(s => s.id === currentStepId);
    if (!currentStep) return null;

    // Check if current step is a substep
    if (currentStep.parent_step_id) {
      const mainStep = steps.find(s => s.id === currentStep.parent_step_id);
      const mainStepIndex = stepsStructure.findIndex(
        struct => struct.mainStep.id === currentStep.parent_step_id
      );
      return {
        currentStep,
        mainStep,
        mainStepIndex,
        isSubstep: true,
      };
    }

    // Current step is a main step
    const mainStepIndex = stepsStructure.findIndex(
      struct => struct.mainStep.id === currentStepId
    );
    return {
      currentStep,
      mainStep: currentStep,
      mainStepIndex,
      isSubstep: false,
    };
  }, [currentStepId, steps, stepsStructure]);

  useEffect(() => {
    loadInstruction();
  }, [instructionId, currentTenantId]);

  const loadInstruction = async () => {
    if (!instructionId || !currentTenantId) return;

    try {
      const { data: instructionData, error: instructionError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('work_instructions')
        .select('*')
        .eq('id', instructionId)
        .eq('tenant_id', currentTenantId)
        .single();

      if (instructionError) throw instructionError;
      setInstruction(instructionData);

      const { data: stepsData, error: stepsError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('instruction_steps')
        .select('*')
        .eq('instruction_id', instructionId)
        .eq('tenant_id', currentTenantId)
        .order('step_number', { ascending: true });

      if (stepsError) throw stepsError;

      // Process all steps (main and substeps)
      const allSteps = (stepsData || []).map(s => ({
        ...s,
        image_url: jsonToImageArray(s.image_url),
        tools_equipment_image_url: jsonToImageArray(s.tools_equipment_image_url),
      }));

      setSteps(allSteps);

      // Set initial current step (first substep of first main step, or first main step)
      const mainSteps = allSteps.filter(s => !s.parent_step_id);
      if (mainSteps.length > 0) {
        const firstMainStep = mainSteps[0];
        const firstSubsteps = allSteps.filter(s => s.parent_step_id === firstMainStep.id);

        // Start with first substep if exists, otherwise first main step
        if (firstSubsteps.length > 0) {
          setCurrentStepId(firstSubsteps[0].id);
        } else {
          setCurrentStepId(firstMainStep.id);
        }
      }

      setLoading(false);
    } catch (error: any) {
      toast.error(error.message || t('errors.loadInstruction'));
      setLoading(false);
    }
  };

  const handleSubstepClick = (substepId: string) => {
    setCurrentStepId(substepId);
  };

  const handleNextMainStep = () => {
    if (!currentStepInfo) return;
    const { mainStepIndex } = currentStepInfo;

    if (mainStepIndex < stepsStructure.length - 1) {
      const nextStructure = stepsStructure[mainStepIndex + 1];
      if (nextStructure.substeps.length > 0) {
        setCurrentStepId(nextStructure.substeps[0].id);
      } else {
        setCurrentStepId(nextStructure.mainStep.id);
      }
    }
  };

  const handlePreviousMainStep = () => {
    if (!currentStepInfo) return;
    const { mainStepIndex } = currentStepInfo;

    if (mainStepIndex > 0) {
      const prevStructure = stepsStructure[mainStepIndex - 1];
      if (prevStructure.substeps.length > 0) {
        setCurrentStepId(prevStructure.substeps[0].id);
      } else {
        setCurrentStepId(prevStructure.mainStep.id);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!instruction || steps.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>{t('instructions.noStepsFound')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              {t('instructions.noStepsDesc')}
            </p>
            <Button onClick={() => navigate('/manager/instructions')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('instructions.backToInstructions')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!currentStepInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { currentStep, mainStep, mainStepIndex, isSubstep } = currentStepInfo;
  const currentMainStepStructure = stepsStructure[mainStepIndex];

  // Prepare main steps for progress bar
  const mainStepsForProgress = stepsStructure.map((struct, index) => ({
    id: struct.mainStep.id,
    step_number: struct.mainStep.step_number,
    title: struct.mainStep.title,
    isCompleted: false, // Preview mode - nothing is completed
    isCurrent: index === mainStepIndex,
  }));

  // Prepare substeps for sidebar
  const substepsForSidebar = currentMainStepStructure.substeps.map(substep => ({
    id: substep.id,
    step_number: substep.step_number,
    title: substep.title,
    description: substep.description,
    isCompleted: false, // Preview mode - nothing is completed
  }));

  return (
    <WorkflowLayout
      header={
        <div className="px-4 py-2">
          <div className="flex items-center justify-between gap-4 mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/manager/instructions')}
              className="h-8"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('instructions.backToInstructions')}
            </Button>
            <div className="flex items-center gap-3 flex-1 justify-center">
              <Badge variant="outline" className="font-mono">
                v{instruction.revision}
              </Badge>
              <Badge variant={instruction.status === 'approved' ? 'default' : 'secondary'}>
                {instruction.status === 'approved' ? t('instructions.approved') : t('instructions.draft')}
              </Badge>
            </div>
            <span className="text-xs text-muted-foreground truncate max-w-48">
              {instruction.title}
            </span>
          </div>

          {/* Main Steps Progress Bar */}
          <WorkflowProgressBar
            mainSteps={mainStepsForProgress}
            currentMainStepIndex={mainStepIndex}
          />
        </div>
      }
      sidebar={
        substepsForSidebar.length > 0 ? (
          <SubstepsSidebar
            substeps={substepsForSidebar}
            currentSubstepId={currentStepId}
            onSubstepClick={handleSubstepClick}
          />
        ) : undefined
      }
    >
      {/* Main Step Navigation */}
      <div className="flex items-center gap-4 mb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePreviousMainStep}
          disabled={mainStepIndex === 0}
          title={t('workflow.previousChapter')}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t('workflow.previousChapter')}
        </Button>
        <div className="flex-1 text-center">
          <h2 className="text-xl font-semibold text-muted-foreground">
            {mainStep?.title}
          </h2>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleNextMainStep}
          disabled={mainStepIndex === stepsStructure.length - 1}
          title={t('workflow.nextChapter')}
        >
          {t('workflow.nextChapter')}
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      <Card className="flex-1">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl flex items-center gap-2">
              {currentStep.title}
            </CardTitle>
            <div className="flex items-center gap-2">
              {currentStep.is_quality_check && (
                <Badge variant="secondary">{t('workflow.qualityCheck')}</Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step Images */}
          {currentStep.image_url.length > 0 && (
            <ImageCarousel images={currentStep.image_url} alt={currentStep.title} />
          )}

          {/* Description */}
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentStep.description) }}
          />

          {/* Part Number */}
          {currentStep.part_number && (
            <div className="p-3 bg-muted rounded-lg">
              <span className="text-sm font-medium">{t('workflow.partNumber')}: </span>
              <span className="font-mono">{currentStep.part_number}</span>
            </div>
          )}

          {/* Tools & Equipment */}
          {currentStep.tools_equipment && (
            <div className="p-3 bg-muted rounded-lg">
              <span className="text-sm font-medium">{t('workflow.toolsEquipment')}: </span>
              <span>{currentStep.tools_equipment}</span>
              {currentStep.tools_equipment_image_url && currentStep.tools_equipment_image_url.length > 0 && (
                <div className="mt-2">
                  <ImageCarousel images={currentStep.tools_equipment_image_url} alt={t('workflow.toolsEquipmentAlt')} />
                </div>
              )}
            </div>
          )}

          {/* PPE */}
          {currentStep.ppe && (
            <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
              <span className="text-sm font-medium text-warning">{t('workflow.ppeRequired')}: </span>
              <span>{currentStep.ppe}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </WorkflowLayout>
  );
};

export default InstructionPreview;
